import * as Sentry from '@sentry/react';
import {SerializedError} from '@reduxjs/toolkit';

/**
 * Error levels.
 */
export type Level = 'error' | 'warn' | 'info' | 'debug' | 'critical';

/**
 * Any of the error possibilities that might come through.
 */
export type AnyError = SerializedError | Error | string | undefined | null;

// Convert internal levels to Sentry levels.
const levelToSeverity = (level: Level) => {
  switch (level) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warning';
    case 'info':
      return 'info';
    case 'debug':
      return 'debug';
    case 'critical':
      return 'fatal';
    default:
      return 'info';
  }
};

/**
 * Get console method from internal log level.
 */
const levelToConsole = (level: Level) => {
  switch (level) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warn';
    case 'debug':
      return 'debug';
    case 'info':
      return 'info';
    case 'critical':
      return 'error';
  }
};

/**
 * Report an event to Sentry.
 */
export const report = (level: Level, msg: string, err?: AnyError) => {
  const hasError = !!err;
  if (!(err instanceof Error)) {
    if (!err) {
      err = new Error(msg);
    } else if (typeof err === 'string') {
      err = new Error(err);
    } else {
      // SerializedError
      const se = err;
      err = new Error(se.message);
      err.stack = se.stack;
      err.name = se.name;
    }
  }

  if (__DEV__) {
    if (hasError) {
      console[levelToConsole(level)](msg, err);
    } else {
      console[levelToConsole(level)](msg);
    }
  } else if (level !== 'error' && level !== 'critical') {
    // Don't bother reporting anything less than an error.
    return;
  }

  Sentry.captureException(err, {
    level: levelToSeverity(level),
    extra: {
      msg,
    },
  });
};

export const error = report.bind(null, 'error');

export const warn = report.bind(null, 'warn');

export const info = report.bind(null, 'info');

export const debug = report.bind(null, 'debug');

export const critical = report.bind(null, 'critical');
